/**
 * Rx-12 — one paper prescription upload, worked on its own page. It was a dialog over the queue,
 * too cramped to read the paper and transcribe it side by side.
 *
 * The uploaded file sits on the left and stays in view while the work scrolls (DocumentViewer:
 * zoom, turn, drag). The work is on the right, by the upload's state: start the review; transcribe
 * it — the prescribing doctor, the medicines, how long it stays valid — or reject it with a reason
 * the patient sees; or, once resolved, what became of it and the next upload waiting. A
 * digitisation in progress is kept in this browser until it is sent (lib/digitiseDraft.ts), so
 * leaving the page loses nothing.
 *
 * The queue's filters ride along in the URL: "Back to the queue" returns to the same view, and
 * Previous / Next walk it. A doctor missing from the master can be added on the spot (DoctorPicker);
 * they aren't told, and can't sign in until an admin allows it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { ApiError, describeError } from "../../api/errors";
import {
  digitiseUpload,
  getRxValidity,
  getUpload,
  rejectUpload,
  startReview,
  type DoctorOption,
  type Upload,
} from "../../api/pharmacist";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { RxLineEditor } from "../../components/RxLineEditor";
import { Modal } from "../../components/ui/Modal";
import { clearDraft, draftStorage, loadDraft, saveDraft } from "../../lib/digitiseDraft";
import { rxLineProblems, rxLinesReady, toRxLineInput, type RxLineDraft } from "../../lib/rxLines";
import {
  formatDateTime,
  formatPhone,
  isTodo,
  personDetails,
  queueFiltersFromParams,
  REJECT_REASONS,
  reviewSummary,
  timeAgo,
  toQueueQuery,
  WAIT_TONE,
  waitLevel,
} from "../../lib/uploadQueue";
import { useNow } from "../../lib/useNow";
import { DocumentViewer } from "./DocumentViewer";
import { DoctorPicker } from "./DoctorPicker";
import { TodayCount, UploadStatusBadge } from "./uploadParts";
import { queuePath, uploadPath, useUploadQueue } from "./uploadQueueData";

const NOTE_MAX = 500;

export function DigitiseUpload() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const now = useNow();
  const carried = params.toString();
  const backTo = queuePath(params);

  // The queue this upload was opened from: its loaded rows show this one at once, and are walked by
  // Previous / Next.
  const query = useMemo(() => toQueueQuery(queueFiltersFromParams(new URLSearchParams(carried))), [carried]);
  const queue = useUploadQueue(query);
  const rows = useMemo(() => queue.data?.pages.flatMap((p) => p.data) ?? [], [queue.data]);
  const total = queue.data?.pages[0]?.total;

  const {
    data: upload,
    isPending,
    error,
  } = useQuery({
    queryKey: ["ph-upload", id],
    queryFn: () => getUpload(id),
    placeholderData: () => rows.find((r) => r.id === id),
    retry: (count, e) => !(e instanceof ApiError && e.status === 404) && count < 2,
  });

  // Where this upload was last seen in the queue. Once decided it leaves a "to do" queue, and the one
  // after it moves up into its place: that one is then next.
  const found = rows.findIndex((r) => r.id === id);
  const [anchor, setAnchor] = useState({ id, at: -1 });
  useEffect(() => {
    if (found >= 0) setAnchor({ id, at: found });
  }, [id, found]);
  const at = found >= 0 ? found : anchor.id === id ? anchor.at : -1;
  const prev = found >= 0 ? rows[found - 1] : at > 0 ? rows[at - 1] : undefined;
  const next = found >= 0 ? rows[found + 1] : at >= 0 ? rows[at] : rows.find((r) => r.id !== id);
  // After a decision: the next one still to do.
  const nextTodo = rows.slice(Math.max(at, 0)).find((r) => r.id !== id && isTodo(r)) ?? rows.find((r) => r.id !== id && isTodo(r));
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = queue;
  useEffect(() => {
    // Near the end of what is loaded: fetch on, so Next keeps going.
    if (at >= 0 && at >= rows.length - 2 && hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [at, rows.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const go = (u: Upload) => navigate(uploadPath(u.id, params));
  const changed = (updated?: Upload) => {
    if (updated) qc.setQueryData(["ph-upload", id], updated);
    void qc.invalidateQueries({ queryKey: ["ph-upload", id] });
    void qc.invalidateQueries({ queryKey: ["ph-uploads"] });
  };

  if (!upload) {
    const missing = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4">
        <Link to={backTo} className="text-sm text-brand hover:underline">
          ← Back to the queue
        </Link>
        {isPending ? (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(30rem,36rem)]" aria-busy="true" aria-label="Loading the upload">
            <div className="h-[60vh] animate-pulse rounded-xl bg-gray-100" />
            <div className="h-80 animate-pulse rounded-xl bg-gray-100" />
          </div>
        ) : (
          <p className="rounded-xl border bg-white p-6 text-sm text-red-600">
            {missing ? "This upload doesn't exist, or the link is wrong." : describeError(error)}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[112rem] flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to={backTo} className="text-sm text-brand hover:underline">
          ← Back to the queue
        </Link>
        <nav aria-label="Uploads in this queue" className="flex items-center gap-1 text-sm">
          <button
            type="button"
            disabled={!prev}
            onClick={() => prev && go(prev)}
            className="rounded-lg px-2.5 py-1.5 text-gray-600 hover:bg-white disabled:opacity-40 disabled:hover:bg-transparent"
          >
            ‹ Previous
          </button>
          {found >= 0 && total !== undefined && (
            <span className="px-1 text-xs tabular-nums text-gray-400">
              {found + 1} of {total}
            </span>
          )}
          <button
            type="button"
            disabled={!next}
            onClick={() => next && go(next)}
            className="rounded-lg px-2.5 py-1.5 font-medium text-brand hover:bg-white disabled:opacity-40 disabled:hover:bg-transparent"
          >
            Next ›
          </button>
        </nav>
      </div>

      <UploadHeader upload={upload} now={now} />

      {/* The paper on the left, the work on the right (stacked below xl: the paper first). */}
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(30rem,36rem)]">
        <DocumentViewer
          key={upload.id}
          uploadId={upload.id}
          className="h-[65vh] min-h-80 xl:sticky xl:top-0 xl:h-[calc(100dvh-9rem)]"
        />
        <WorkPanel
          key={upload.id}
          upload={upload}
          now={now}
          onChanged={changed}
          next={nextTodo}
          onNext={() => nextTodo && go(nextTodo)}
          backTo={backTo}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- who / when

function UploadHeader({ upload: u, now }: { upload: Upload; now: number }) {
  const todo = isTodo(u);
  const atLimit = u.digitized_today >= u.digitize_daily_limit;
  const holder = personDetails(u.uploaded_by);
  const member = u.for_member ? personDetails(u.for_member) : "";
  return (
    <section className="rounded-xl border bg-white p-4 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">
              Upload <span className="font-mono">#{u.id.slice(0, 8)}</span>
            </h2>
            <UploadStatusBadge status={u.status} />
          </div>
          <p className="mt-0.5 text-sm text-gray-500">
            Uploaded{" "}
            <span className={todo ? WAIT_TONE[waitLevel(u.created_at, now)] : ""} title={formatDateTime(u.created_at)}>
              {timeAgo(u.created_at, now)}
            </span>{" "}
            · {formatDateTime(u.created_at)} · {reviewSummary(u, now)}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          Digitised today for this account
          <TodayCount used={u.digitized_today} limit={u.digitize_daily_limit} />
        </div>
      </div>

      <dl className="mt-3 grid gap-x-6 gap-y-3 border-t pt-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="Uploaded by">
          <span className="font-medium">{u.uploaded_by.name || "Unnamed patient"}</span>
          {u.uploaded_by.phone && (
            <a href={`tel:${u.uploaded_by.phone}`} className="ml-1 text-brand hover:underline">
              {formatPhone(u.uploaded_by.phone)}
            </a>
          )}
          {holder && <span className="block text-xs text-gray-500">{holder}</span>}
        </Fact>
        <Fact label="For">
          {u.for_member ? (
            <>
              <span className="font-medium">{u.for_member.name || "Family member"}</span>
              {member && <span className="block text-xs text-gray-500">{member}</span>}
            </>
          ) : (
            "The account holder"
          )}
        </Fact>
        <Fact label="Patient's note">
          {u.note ? <span className="whitespace-pre-wrap break-words">“{u.note}”</span> : <span className="text-gray-400">None</span>}
        </Fact>
      </dl>

      {atLimit && todo && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This account has reached today's limit of {u.digitize_daily_limit} digitised prescriptions: digitising this one will be
          refused until tomorrow. Your work here is kept until then.
        </p>
      )}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-gray-800">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------- the work

// The upload was cancelled by the patient, or resolved by someone else, meanwhile (ORD-07): the
// server says which; the page re-reads the upload, which then shows how it ended (PHA-09).
const RESOLVED_MEANWHILE = new Set(["UPLOAD_NOT_PENDING", "UPLOAD_ALREADY_RESOLVED"]);

function actionFailed(e: unknown, showInline: (message: string) => void, refresh: () => void): void {
  if (e instanceof ApiError && RESOLVED_MEANWHILE.has(e.code)) {
    toast.error(describeError(e));
    refresh();
    return;
  }
  showInline(describeError(e));
}

function WorkPanel({
  upload,
  now,
  onChanged,
  next,
  onNext,
  backTo,
}: {
  upload: Upload;
  now: number;
  onChanged: (updated?: Upload) => void;
  next: Upload | undefined;
  onNext: () => void;
  backTo: string;
}) {
  const [rejecting, setRejecting] = useState(false);
  let body: ReactNode;
  if (upload.status === "pending_review") body = <StartReview upload={upload} onChanged={onChanged} onReject={() => setRejecting(true)} />;
  else if (upload.status === "in_review")
    body = <DigitiseForm upload={upload} now={now} onChanged={onChanged} onReject={() => setRejecting(true)} />;
  else body = <Outcome upload={upload} now={now} next={next} onNext={onNext} backTo={backTo} />;

  return (
    <div className="min-w-0 space-y-4">
      {body}
      <RejectDialog upload={upload} open={rejecting} onClose={() => setRejecting(false)} onChanged={onChanged} />
    </div>
  );
}

function StartReview({ upload, onChanged, onReject }: { upload: Upload; onChanged: (u?: Upload) => void; onReject: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const start = useMutation({
    mutationFn: () => startReview(upload.id),
    onMutate: () => setError(null),
    onSuccess: (u) => onChanged(u),
    onError: (e) => actionFailed(e, setError, () => onChanged()),
  });
  return (
    <section className="rounded-xl border bg-white p-5 shadow-xs">
      <h3 className="font-semibold text-gray-900">Not picked up yet</h3>
      <p className="mt-1 text-sm text-gray-600">
        Start the review to take this upload: the queue then shows it as in review by you. Next, read the prescription and
        transcribe it, or reject it if it can't be used.
      </p>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => start.mutate()}
          disabled={start.isPending}
          className="rounded-lg bg-brand px-5 py-2 text-sm font-medium text-brand-fg disabled:opacity-50"
        >
          {start.isPending ? "Starting…" : "Start review"}
        </button>
        <button type="button" onClick={onReject} className="rounded-lg border border-red-200 px-4 py-2 text-sm text-red-700 hover:bg-red-50">
          Reject…
        </button>
      </div>
    </section>
  );
}

// Strictest first: the class that limits a prescription's validity the most.
const CLASS_RANK: Record<string, number> = { none: 0, H: 1, H1: 2, X: 3 };
const strictestClass = (classes: string[]): string =>
  classes.reduce((worst, c) => ((CLASS_RANK[c] ?? 0) > (CLASS_RANK[worst] ?? 0) ? c : worst), "none");
const classLabel = (cls: string): string => (cls === "none" ? "an OTC prescription" : `Schedule ${cls}`);

function DigitiseForm({
  upload,
  now,
  onChanged,
  onReject,
}: {
  upload: Upload;
  now: number;
  onChanged: (u?: Upload) => void;
  onReject: () => void;
}) {
  // The draft kept from an earlier visit, if any (read once, when the form opens).
  const [restored, setRestored] = useState(() => loadDraft(draftStorage(), upload.id));
  const [doctor, setDoctor] = useState<DoctorOption | null>(restored?.doctor ?? null);
  const [lines, setLines] = useState<RxLineDraft[]>(restored?.lines ?? []);
  const [validDays, setValidDays] = useState(restored?.validDays ?? "");
  const [notes, setNotes] = useState(restored?.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    saveDraft(draftStorage(), upload.id, { doctor, lines, validDays, notes });
  }, [upload.id, doctor, lines, validDays, notes]);
  const startOver = () => {
    clearDraft(draftStorage(), upload.id);
    setDoctor(null);
    setLines([]);
    setValidDays("");
    setNotes("");
    setRestored(null);
  };

  // How long the Rx stays orderable (D-08): the strictest medicine's class sets the maximum, and
  // the pharmacist may shorten it (e.g. an older paper prescription). Blank = the maximum.
  const { data: validity } = useQuery({ queryKey: ["rx-validity"], queryFn: getRxValidity, staleTime: 5 * 60_000 });
  const strictest = strictestClass(lines.map((l) => l.schedule_class));
  const maxDays = validity?.validity_days[strictest];
  const typedDays = validDays.trim() === "" ? null : Number(validDays);
  const validityProblem =
    typedDays === null
      ? null
      : !Number.isInteger(typedDays) || typedDays < 1
        ? "Enter a whole number of days."
        : maxDays !== undefined && typedDays > maxDays
          ? `At most ${maxDays} days for ${classLabel(strictest)}.`
          : null;

  const atLimit = upload.digitized_today >= upload.digitize_daily_limit;
  const badLines = lines.filter((l) => Object.values(rxLineProblems(l)).some(Boolean)).length;
  const missing: string[] = [];
  if (!doctor) missing.push("the prescribing doctor");
  if (lines.length === 0) missing.push("at least one medicine");
  else if (badLines) missing.push(`the details of ${badLines} ${badLines === 1 ? "medicine" : "medicines"}`);
  if (validityProblem) missing.push("a valid number of days");
  const ready = missing.length === 0 && rxLinesReady(lines);

  // A retry or double click of the same digitisation sends the same key: never a second Rx.
  const submitKey = useIdempotencyKey();
  const submit = useMutation({
    mutationFn: () => {
      const body = { notes: notes.trim() || null, validity_days: typedDays, lines: lines.map(toRxLineInput) };
      return digitiseUpload(upload.id, doctor!.id, body, submitKey.keyFor({ doctorId: doctor!.id, ...body }));
    },
    onMutate: () => setError(null),
    onSuccess: () => {
      submitKey.reset();
      clearDraft(draftStorage(), upload.id);
      toast.success("Prescription created.");
      onChanged();
    },
    onError: (e) => actionFailed(e, setError, () => onChanged()),
  });

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setTried(true);
        if (ready && !atLimit) submit.mutate();
      }}
      className="space-y-4"
    >
      {restored && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-800">
          <span>Your unsent work from {timeAgo(new Date(restored.savedAt).toISOString(), now)} is back.</span>
          <button type="button" onClick={startOver} className="font-medium underline hover:no-underline">
            Start over
          </button>
        </div>
      )}

      <Step
        n={1}
        title="Prescribing doctor"
        hint="The doctor whose name is on the paper prescription. Not listed? Add them as printed."
        done={!!doctor}
      >
        <DoctorPicker value={doctor} onChange={setDoctor} />
      </Step>

      <Step
        n={2}
        title="Medicines"
        hint="Search the catalogue and add each medicine as written, with its dose and timing."
        done={rxLinesReady(lines)}
        aside={lines.length > 0 && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs tabular-nums text-gray-600">{lines.length}</span>}
      >
        <RxLineEditor lines={lines} setLines={setLines} />
      </Step>

      <Step n={3} title="Validity and notes" hint="Optional: how long it can be ordered from, and anything worth keeping with it." done={!validityProblem}>
        <div className="grid gap-4 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-600">Valid for (days)</span>
            <input
              inputMode="numeric"
              value={validDays}
              onChange={(e) => setValidDays(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder={maxDays !== undefined ? `${maxDays} (the most)` : "the most allowed"}
              aria-invalid={!!validityProblem}
              className={`rounded-lg border px-3 py-2 ${validityProblem ? "border-red-400" : ""}`}
            />
            <span className={`text-xs ${validityProblem ? "text-red-600" : "text-gray-400"}`}>
              {validityProblem ??
                (maxDays !== undefined
                  ? `Up to ${maxDays} days for ${classLabel(strictest)}, from today. Shorten it for an older paper prescription.`
                  : "Leave blank for the most this prescription allows.")}
            </span>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-600">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. a changing dose, or what was hard to read"
              className="rounded-lg border px-3 py-2"
            />
          </label>
        </div>
      </Step>

      {/* Stays at the bottom of the screen while the form scrolls, from lg up (below, the tab bar
          would cover it). */}
      <div className="z-10 space-y-2 rounded-xl border bg-white p-3 shadow-raised lg:sticky lg:bottom-4">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {atLimit ? (
          <p className="text-sm text-amber-800">Today's limit for this account is reached: create it tomorrow (this draft is kept).</p>
        ) : missing.length > 0 ? (
          <p className={`text-sm ${tried ? "text-red-600" : "text-gray-500"}`}>Still needed: {missing.join(", ")}.</p>
        ) : (
          <p className="text-sm text-emerald-700">
            Ready: {lines.length} {lines.length === 1 ? "medicine" : "medicines"}, by {doctor?.full_name}
            {typedDays ? `, valid ${typedDays} days` : ""}.
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
          <button type="button" onClick={onReject} className="rounded-lg border border-red-200 px-4 py-2 text-sm text-red-700 hover:bg-red-50">
            Reject…
          </button>
          <span className="hidden sm:block sm:flex-1" />
          <button
            type="submit"
            disabled={submit.isPending || atLimit}
            className={`rounded-lg px-5 py-2 text-sm font-medium transition disabled:opacity-50 ${
              ready ? "bg-brand text-brand-fg" : "bg-brand/60 text-brand-fg"
            }`}
          >
            {submit.isPending ? "Creating prescription…" : "Create prescription"}
          </button>
        </div>
      </div>
    </form>
  );
}

function Step({
  n,
  title,
  hint,
  done,
  aside,
  children,
}: {
  n: number;
  title: string;
  hint?: string;
  done: boolean;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border bg-white p-4 shadow-xs">
      <header className="mb-3 flex items-start gap-3">
        <span
          aria-hidden
          className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-semibold ${
            done ? "bg-emerald-600 text-white" : "bg-brand-50 text-brand"
          }`}
        >
          {done ? "✓" : n}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-gray-900">{title}</h3>
          {hint && <p className="text-xs text-gray-500">{hint}</p>}
        </div>
        {aside}
      </header>
      {children}
    </section>
  );
}

function Outcome({
  upload: u,
  now,
  next,
  onNext,
  backTo,
}: {
  upload: Upload;
  now: number;
  next: Upload | undefined;
  onNext: () => void;
  backTo: string;
}) {
  const reason = REJECT_REASONS.find((r) => r.value === u.reject_reason)?.label ?? u.reject_reason;
  const by = `By ${u.reviewed_by_name ?? "a pharmacist"}${u.reviewed_at ? `, ${timeAgo(u.reviewed_at, now)}` : ""}.`;
  const [icon, tone, title, detail] =
    u.status === "digitized"
      ? ["✓", "bg-emerald-600 text-white", `Digitised${u.digitized_prescription_ref ? ` as ${u.digitized_prescription_ref}` : ""}`, by]
      : u.status === "rejected"
        ? [
            "✕",
            "bg-red-600 text-white",
            `Rejected${reason ? `: ${reason}` : ""}`,
            `${u.reject_note ? `“${u.reject_note}” ` : ""}${by} The patient sees the reason${u.reject_note ? " and the note" : ""}.`,
          ]
        : ["–", "bg-gray-400 text-white", "Cancelled by the patient", "There is nothing to do with this upload."];
  return (
    <section className="rounded-xl border bg-white p-5 shadow-xs">
      <div className="flex items-start gap-3">
        <span aria-hidden className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-lg ${tone}`}>
          {icon}
        </span>
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="mt-0.5 break-words text-sm text-gray-600">{detail}</p>
        </div>
      </div>
      <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
        {next ? (
          <button type="button" onClick={onNext} className="rounded-lg bg-brand px-5 py-2 text-sm font-medium text-brand-fg">
            Next upload: {next.uploaded_by.name || `#${next.id.slice(0, 8)}`} ›
          </button>
        ) : (
          <span className="text-sm text-gray-500">Nothing else is waiting in this view.</span>
        )}
        <Link to={backTo} className="rounded-lg border px-4 py-2 text-center text-sm hover:bg-gray-50">
          Back to the queue
        </Link>
      </div>
    </section>
  );
}

function RejectDialog({
  upload,
  open,
  onClose,
  onChanged,
}: {
  upload: Upload;
  open: boolean;
  onClose: () => void;
  onChanged: (u?: Upload) => void;
}) {
  const [reason, setReason] = useState("illegible");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const noteNeeded = reason === "other" && !note.trim();
  const reject = useMutation({
    mutationFn: () => rejectUpload(upload.id, reason, note.trim() || null),
    onMutate: () => setError(null),
    onSuccess: (u) => {
      clearDraft(draftStorage(), upload.id);
      toast.success("Upload rejected.");
      onClose();
      onChanged(u);
    },
    onError: (e) =>
      actionFailed(e, setError, () => {
        onClose();
        onChanged();
      }),
  });
  return (
    <Modal open={open} title="Reject this upload" onClose={() => !reject.isPending && onClose()}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!noteNeeded) reject.mutate();
        }}
        className="space-y-4 text-sm"
      >
        <fieldset className="space-y-2">
          <legend className="mb-1 text-gray-600">Why can't it be digitised?</legend>
          {REJECT_REASONS.map((r) => (
            <label
              key={r.value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 ${reason === r.value ? "border-red-300 bg-red-50/50" : "hover:bg-gray-50"}`}
            >
              <input type="radio" name="reject-reason" value={r.value} checked={reason === r.value} onChange={() => setReason(r.value)} className="mt-1" />
              <span>
                <span className="font-medium text-gray-900">{r.label}</span>
                <span className="block text-xs text-gray-500">{r.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <label className="flex flex-col gap-1">
          <span className="text-gray-600">Note {reason === "other" ? "(needed)" : "(optional)"}</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={NOTE_MAX}
            rows={3}
            placeholder="e.g. The second medicine's name is cut off; please upload a clearer photo."
            className="rounded-lg border px-3 py-2"
          />
          <span className="text-xs text-gray-400">The patient sees the reason and this note.</span>
        </label>
        {error && <p className="text-red-600">{error}</p>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={reject.isPending} className="rounded-lg border px-4 py-2">
            Cancel
          </button>
          <button
            type="submit"
            disabled={reject.isPending || noteNeeded}
            className="rounded-lg bg-red-600 px-4 py-2 font-medium text-white disabled:opacity-50"
          >
            {reject.isPending ? "Rejecting…" : "Reject upload"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
